import axios from 'axios';

import { LOCAL_STORAGE_KEY } from '@common/constants';
import { ApiUrl } from '@common/enums';
import { getStorageData } from '@core/services/local-storage-info';

import { LookupRetailerApiParams, LookupRetailerApiResponse } from './lookupRetailerApi.types';

const MAX_RETRIES = 3;

/**
 * The hosts for the lookup API based on environment
 */
const LOOKUP_HOSTS = {
    production: 'https://ee-api-ssi.prod.inscyth.com',
    staging: 'https://ee-api-ssi.staging.inscyth.com',
};

const selectRetailerLookupParams = (): LookupRetailerApiParams => {
    const clientHost = window.location.hostname;

    /* useLocalParams is true if we're running on localhost and can't determine the retailer from the hostname */
    const useLocalParams = /^(localhost|127\.0\.0\.1)$/i.test(clientHost);

    /**
     * lookupRetailerParams = `{clientHost: string, apiHostname?: string, companyId?: number}`
     * This is only used when running on localhost - it allows us to simulate different retailer environments
     * by setting the localStorage item 'lookupRetailerParams' to a JSON string of the above object
     * e.g. {"clientHost":"test.retailer.com","apiHostname":"https://api.retailer.com","companyId":12345}
     * If not set, it will default to using localhost as the clientHost and no apiHostname or companyId
     */
    const lookupRetailerParams = useLocalParams
        ? getStorageData<LookupRetailerApiParams>(LOCAL_STORAGE_KEY.LOOKUP_PARAMS)
        : undefined;

    return {
        ...lookupRetailerParams,
        clientHost: lookupRetailerParams?.clientHost || clientHost,
    };
};

/**
 * determine which host to use for the lookup API based on the clientHost
 * @param clientHost the hostname to use for lookups
 */
const selectLookupApiHost = (clientHost: string) =>
    clientHost.match(/[.-]+(staging|dev)\./gi) ? LOOKUP_HOSTS.staging : LOOKUP_HOSTS.production;

/**
 * These are the parameters for the lookup API request based on the current hostname or localStorage override
 */
export const LOOKUP_API_PARAMS = selectRetailerLookupParams();

/**
 * The host for the lookup API based on the clientHost that will be looked up
 * NOTE: This is here to avoid any logic in the cloudfront servers.
 *  Necessary when staging and production configs or lookup functions differ.
 */
export const LOOKUP_API_HOST = new URL(
    ApiUrl.LOOKUP_RETAILER, //path only, base URL is determined below
    selectLookupApiHost(LOOKUP_API_PARAMS.clientHost) //base URL
).toString();

/**
 * Perform the actual API request with retries
 * @param params
 */
export const performRequest = async (params: LookupRetailerApiParams): Promise<LookupRetailerApiResponse> => {
    for (let retries = 0; retries < MAX_RETRIES; retries++) {
        try {
            const response = await axios.get<LookupRetailerApiResponse>(LOOKUP_API_HOST, {
                params,
            });

            return response.data;
        } catch (error) {
            if (retries >= MAX_RETRIES - 1) {
                throw error;
            }

            // Wait before retrying (exponential backoff)
            await new Promise((resolve) => setTimeout(resolve, Math.pow(2, retries + 1) * 1000));
        }
    }

    throw new Error('Max retries exceeded');
};
